import React, { useState, useEffect } from "react";
import { Link, useRouter } from "../../../util/router";
import Meta from "../../../components/Meta";
import Section from "../../../components/Section";
import { useAuth } from "../../../util/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { firebaseApp } from "../../../util/firebase";
import PasswordSetupModal from "../../../components/Custom/PasswordSetupModal";
import PageLoader from "../../../components/PageLoader";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { trackPurchase } from "../../../util/analytics";

const functions = getFunctions(firebaseApp);

function WelcomeOnboarding(props) {
  const router = useRouter();
  const auth = useAuth();
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [setupToken, setSetupToken] = useState(null);
  const [tokenError, setTokenError] = useState(null);
  const [signingIn, setSigningIn] = useState(false);

  // Track Purchase event on mount (before token handling strips URL params)
  useEffect(() => {
    if (router.query.paid === 'true' && router.query.product) {
      trackPurchase(router.query.product);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Token recovery state
  const [showRecoveryForm, setShowRecoveryForm] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [requestingNewLink, setRequestingNewLink] = useState(false);
  const [newLinkMessage, setNewLinkMessage] = useState(null);

  useEffect(() => {
    const handleTokenSignin = async () => {
      const token = router.query.token;

      if (!token) {
        // No token - normal page view
        return;
      }

      setSigningIn(true);

      try {
        // Validate token and get custom token for auto-signin
        const validateToken = httpsCallable(functions, 'validatePasswordSetupToken');
        const result = await validateToken({ token });

        if (!result.data.valid) {
          throw new Error('Invalid token');
        }

        // Auto-signin with custom token
        await auth.signInWithCustomToken(result.data.customToken);

        // Store token for password modal
        setSetupToken(token);
        setShowPasswordModal(true);

        // Clear token from URL (security) but preserve next param for post-setup redirect
        const nextParam = router.query.next;
        const cleanUrl = nextParam
          ? `${router.pathname}?next=${encodeURIComponent(nextParam)}`
          : router.pathname;
        router.replace(cleanUrl);

        setSigningIn(false);

      } catch (error) {
        console.error('Token validation error:', error);

        // If token already used, redirect to sign in
        if (error.code === 'functions/failed-precondition') {
          console.log('Token already used, redirecting to sign in...');
          router.push('/auth/signin?message=already_setup');
          return;
        }

        let errorMessage = 'Invalid or expired setup link. Please check your email for the correct link.';

        if (error.code === 'functions/deadline-exceeded') {
          errorMessage = 'Your setup link has expired. Please check your email for a new link.';
        } else if (error.code === 'functions/resource-exhausted') {
          errorMessage = 'Too many attempts. Please request a new link.';
        } else if (error.code === 'functions/not-found') {
          errorMessage = 'Invalid setup link. Please check your email for the correct link.';
        }

        setTokenError(errorMessage);
        setSigningIn(false);
      }
    };

    handleTokenSignin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.query.token]);

  const handlePasswordSetupComplete = () => {
    setShowPasswordModal(false);
    setSetupToken(null);
    // If there's a next param, redirect there after password setup
    if (router.query.next) {
      router.push(router.query.next);
    }
    // Otherwise stay on confirmation page so user can read it
  };

  // Check if the error is recoverable
  const isRecoverableError = tokenError && (
    tokenError.includes('expired') ||
    tokenError.includes('attempts') ||
    tokenError.includes('request a new link')
  );

  // Token recovery handler
  const handleRequestNewLink = async (e) => {
    e.preventDefault();

    if (!recoveryEmail.trim()) {
      setNewLinkMessage({ type: 'error', text: 'Please enter your email address.' });
      return;
    }

    setRequestingNewLink(true);
    setNewLinkMessage(null);

    try {
      const regenerateToken = httpsCallable(functions, 'regeneratePasswordSetupToken');
      const result = await regenerateToken({ email: recoveryEmail.trim() });

      setNewLinkMessage({
        type: 'success',
        text: result.data.message
      });
      setShowRecoveryForm(false);
    } catch (error) {
      console.error('Token regeneration error:', error);

      let errorMsg = 'Failed to send new link. Please try again.';
      if (error.code === 'functions/resource-exhausted') {
        errorMsg = 'Too many requests. Please try again in an hour.';
      } else if (error.code === 'functions/failed-precondition') {
        errorMsg = error.message || 'Password has already been set. Please sign in normally.';
      }

      setNewLinkMessage({ type: 'error', text: errorMsg });
    } finally {
      setRequestingNewLink(false);
    }
  };

  if (signingIn) {
    return <PageLoader />;
  }

  return (
    <>
      <Meta title="Welcome to Insurance Agency Training" />

      {tokenError && (
        <div className="max-w-2xl mx-auto mt-6 mb-0 px-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-yellow-800 mb-3">{tokenError}</p>

            {isRecoverableError && !showRecoveryForm && newLinkMessage?.type !== 'success' && (
              <button
                onClick={() => setShowRecoveryForm(true)}
                className="text-green-700 underline hover:text-green-800"
              >
                Request a new setup link
              </button>
            )}

            {showRecoveryForm && (
              <form onSubmit={handleRequestNewLink} className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Enter your email to receive a new setup link:
                </label>
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={recoveryEmail}
                    onChange={(e) => setRecoveryEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                    disabled={requestingNewLink}
                  />
                  <button
                    type="submit"
                    disabled={requestingNewLink}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                  >
                    {requestingNewLink ? 'Sending...' : 'Send Link'}
                  </button>
                </div>
              </form>
            )}

            {newLinkMessage && (
              <p className={`mt-3 ${newLinkMessage.type === 'success' ? 'text-green-700' : 'text-red-600'}`}>
                {newLinkMessage.text}
              </p>
            )}
          </div>
        </div>
      )}

      <Section
        size="md"
        bgColor="bg-white"
        textColor="text-gray-900"
      >
        <div className="container max-w-3xl mx-auto px-4">
          <div className="text-center mb-8">
            <CheckCircleIcon className="h-20 w-20 text-green-700 mx-auto mb-4" />
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Welcome to Insurance Agency Training!
            </h1>
            <p className="text-xl text-gray-600">
              Your account has been created successfully.
            </p>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              What's Next?
            </h2>
            <ul className="space-y-4">
              <li className="flex items-start">
                <CheckCircleIcon className="h-6 w-6 text-green-700 mr-3 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-gray-900">Explore Our Tools</h3>
                  <p className="text-gray-600">
                    Access our suite of insurance agency tools to help you grow your business.
                  </p>
                </div>
              </li>
              {auth.user && !auth.user.completeInfo && (
                <li className="flex items-start">
                  <CheckCircleIcon className="h-6 w-6 text-green-700 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Complete Your Profile</h3>
                    <p className="text-gray-600">
                      Fill out your profile information to personalize your experience.
                    </p>
                  </div>
                </li>
              )}
              <li className="flex items-start">
                <CheckCircleIcon className="h-6 w-6 text-green-700 mr-3 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-gray-900">Browse Training Options</h3>
                  <p className="text-gray-600">
                    Check out our training programs to take your agency to the next level.
                  </p>
                </div>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-6 w-6 text-green-700 mr-3 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-gray-900">Join the Community</h3>
                  <p className="text-gray-600">
                    Connect with other insurance professionals and share your success.
                  </p>
                </div>
              </li>
            </ul>
          </div>

          <div className="bg-gray-100 rounded-lg p-6 mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-3">
              Need Help?
            </h2>
            <p className="text-gray-600 mb-4">
              If you have any questions or need assistance, our support team is here to help.
            </p>
            <Link to="/support">
              <button className="bg-green-700 text-white px-6 py-2 rounded-lg hover:bg-green-600">
                Visit Help Center
              </button>
            </Link>
          </div>

          <div className="text-center">
            <Link to={router.query.next || "/tools/ip"}>
              <button className="bg-green-700 text-white px-8 py-3 rounded-lg text-lg font-semibold hover:bg-green-600">
                {router.query.next ? "Continue" : "Go to Dashboard"}
              </button>
            </Link>
          </div>
        </div>
      </Section>

      {/* Password Setup Modal */}
      <PasswordSetupModal
        isOpen={showPasswordModal}
        token={setupToken}
        onComplete={handlePasswordSetupComplete}
      />
    </>
  );
}

export default WelcomeOnboarding;
